// src/components/GuideModal.jsx
import React, { useState, useRef, useEffect } from "react";
import "./css/GuideModal.css";
import { db, auth } from "../firebase";
import {
    doc, setDoc, serverTimestamp, writeBatch, collection,
    getDocs,
    updateDoc
} from "firebase/firestore";
import { normalizeSlug } from "../utils/slug";

export const GUIDE_JSON = {
    title: "Chiang Rai",
    slug: "chiang-rai",
    tags: [
        "chiang rai",
        "chiang rai thailand",
        "chiang rai travel guide",
        "things to do in chiang rai",
        "northern thailand travel",
        "white temple thailand",
        "golden triangle thailand",
        "chiang rai itinerary",
        "chiang rai temples",
        "thailand cultural destinations"
    ],

    searchableText:
        "chiang rai thailand travel guide northern thailand white temple wat rong khun blue temple golden triangle chiang rai temples mountains tea plantations night bazaar cultural destination things to do in chiang rai",

    country: {
        type: "country",
        name: "Thailand",
        code: "TH",
        continent: "Asia",
        currency: "THB",
        language: "Thai",
        flag: "🇹🇭"
    },

    city: {
        type: "city",
        name: "Chiang Rai",
        state: "Chiang Rai Province",
        population: "200K",
        timezone: "ICT (UTC+7)"
    },

    guide: {
        id: "chiang-rai",
        title: "Chiang Rai Travel Guide",
        subtitle: "White Temple, mountain scenery & northern Thai culture.",
        pill: "City Travel Guide",
        heroClass: "chiang-rai",
        heroImage: ""
    },

    quickFacts: {
        region: "Northern Thailand",
        bestMonths: "Nov–Feb",
        idealDuration: "2–3 days",
        dailyBudgetRange: "฿1,000–3,000",
        walkability: "Moderate (scooter recommended for temples)"
    },

    sections: {
        whyVisit: {
            title: "Why visit Chiang Rai?",
            description:
                "Chiang Rai is known for its stunning modern temples like the White Temple, scenic mountains, tea plantations, and its relaxed northern Thai atmosphere. It offers a quieter and more cultural experience than Chiang Mai."
        },

        bestTimeToVisit: {
            title: "Best time to visit",
            seasons: [
                { name: "Cool Season", emoji: "🌤️", months: "Nov–Feb", note: "Best weather & clear skies" },
                { name: "Hot Season", emoji: "🔥", months: "Mar–May", note: "Warm temperatures" },
                { name: "Rainy Season", emoji: "🌧️", months: "Jun–Oct", note: "Green landscapes & fewer tourists" }
            ]
        },

        neighborhoods: {
            title: "Popular areas",
            areas: [
                { name: "City Center", emoji: "🏙️", highlights: "Night Bazaar & local markets" },
                { name: "White Temple Area", emoji: "🤍", highlights: "Wat Rong Khun" },
                { name: "Blue Temple Area", emoji: "💙", highlights: "Wat Rong Suea Ten" },
                { name: "Golden Triangle", emoji: "🌄", highlights: "Scenic river views & borders" }
            ]
        },

        budget: {
            title: "Estimated daily budget",
            tiers: [
                { type: "Backpacker", range: "฿1,000–1,800" },
                { type: "Mid-range", range: "฿2,000–3,000", highlight: true },
                { type: "Luxury", range: "฿5,000+" }
            ]
        },

        gettingAround: {
            title: "Getting around Chiang Rai",
            description:
                "Scooter rentals are common for visiting temples outside the city. Grab and taxis are available. Many attractions are spread out, so planning transport is recommended."
        },

        cta: {
            title: "Planning a Chiang Rai trip?",
            subtitle: "Save this guide or add it to your northern Thailand itinerary.",
            actions: ["Add Trip", "Save Guide"]
        }
    },

    travelGuide: {
        wifiAndInternet: {
            title: "Wi-Fi & Internet",
            tips: [
                "Wi-Fi available in hotels & cafés",
                "Good mobile coverage",
                "Reliable 4G/5G networks"
            ]
        },

        simAndEsim: {
            title: "SIM & eSIM",
            options: [
                "Tourist SIMs available at airport",
                "Affordable prepaid packages",
                "Top providers: AIS, TrueMove, DTAC"
            ],
            tip: "Strong coverage across city and outskirts"
        },

        moneyAndPayments: {
            title: "Money & Payments",
            currency: "Thai Baht (฿)",
            tips: [
                "Cash useful in markets",
                "Cards accepted in hotels",
                "ATMs available in town"
            ]
        },

        transportAndCabs: {
            title: "Transport & Cabs",
            tips: [
                "Rent scooter for flexibility",
                "Use Grab for city trips",
                "Book tours for Golden Triangle"
            ]
        },

        essentialApps: {
            title: "Best Apps to Install",
            categories: [
                { category: "Transport", apps: ["Grab", "Google Maps"] },
                { category: "Travel", apps: ["Tripadvisor", "Google Translate"] },
                { category: "Tours", apps: ["Klook"] },
                { category: "Payments", apps: ["Wise", "Revolut"] }
            ]
        },

        safetyAndTips: {
            title: "Safety & Local Tips",
            tips: [
                "Dress modestly at temples",
                "Carry water when exploring",
                "Be cautious riding scooters",
                "Start temple visits early"
            ]
        }
    },

    gallery: [],

    placesToVisit: [
        { label: "Wat Rong Khun (White Temple)", placeId: "wat-rong-khun" },
        { label: "Wat Rong Suea Ten (Blue Temple)", placeId: "wat-rong-suea-ten" },
        { label: "Wat Huay Pla Kang", placeId: "wat-huay-pla-kang" },
        { label: "Golden Triangle", placeId: "golden-triangle" },
        { label: "Singha Park", placeId: "singha-park" },
        { label: "Chiang Rai Night Bazaar", placeId: "chiang-rai-night-bazaar" },
        { label: "Baan Dam Museum", placeId: "baan-dam-museum" },
        { label: "Khun Korn Waterfall", placeId: "khun-korn-waterfall" }
    ]
};

const PLACES_FOR_UPLOAD = [
    {
        id: "wat-rong-khun",
        slug: "wat-rong-khun",
        name: "Wat Rong Khun (White Temple)",
        city: "chiang-rai",
        country: "thailand",
        guidesId: "chiang-rai",
        type: "temple",
        shortDescription:
            "Famous white contemporary temple designed by Chalermchai Kositpipat.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Paid",
        location: { lat: 19.824, lng: 99.763 },
        images: [],
        tips: ["Arrive early", "Very photogenic", "Dress modestly"],

        tags: [
            "wat rong khun",
            "white temple chiang rai",
            "wat rong khun thailand",
            "things to do in chiang rai",
            "chiang rai temples",
            "famous temples in thailand",
            "northern thailand attractions",
            "chiang rai landmarks"
        ],

        searchableText:
            "wat rong khun white temple chiang rai thailand famous white contemporary temple chalermchai kositpipat chiang rai temples northern thailand attractions things to do in chiang rai"
    },

    {
        id: "wat-rong-suea-ten",
        slug: "wat-rong-suea-ten",
        name: "Wat Rong Suea Ten (Blue Temple)",
        city: "chiang-rai",
        country: "thailand",
        guidesId: "chiang-rai",
        type: "temple",
        shortDescription: "Vibrant blue temple with modern Buddhist art.",
        recommendedTime: "1 hour",
        bestTimeToVisit: "Morning",
        entryFee: "Free",
        location: { lat: 19.9135, lng: 99.8322 },
        images: [],
        tips: ["Stunning interior", "Short visit needed", "Good photo spot"],

        tags: [
            "wat rong suea ten",
            "blue temple chiang rai",
            "chiang rai temples",
            "things to do in chiang rai",
            "modern temples thailand",
            "chiang rai attractions",
            "northern thailand landmarks"
        ],

        searchableText:
            "wat rong suea ten blue temple chiang rai thailand modern buddhist temple chiang rai temples northern thailand attractions things to do in chiang rai"
    },

    {
        id: "golden-triangle",
        slug: "golden-triangle",
        name: "Golden Triangle",
        city: "chiang-rai",
        country: "thailand",
        guidesId: "chiang-rai",
        type: "landmark",
        shortDescription:
            "Meeting point of Thailand, Laos, and Myanmar.",
        recommendedTime: "2 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Free",
        location: { lat: 20.353, lng: 100.0867 },
        images: [],
        tips: ["Scenic river views", "Boat tours available", "Historical significance"],

        tags: [
            "golden triangle thailand",
            "chiang rai golden triangle",
            "thailand laos myanmar border",
            "things to do in chiang rai",
            "northern thailand landmarks",
            "chiang rai attractions"
        ],

        searchableText:
            "golden triangle chiang rai thailand laos myanmar border landmark northern thailand attractions things to do in chiang rai scenic river views"
    },

    {
        id: "chiang-rai-night-bazaar",
        slug: "chiang-rai-night-bazaar",
        name: "Chiang Rai Night Bazaar",
        city: "chiang-rai",
        country: "thailand",
        guidesId: "chiang-rai",
        type: "market",
        shortDescription:
            "Evening market with local crafts and street food.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Evening",
        entryFee: "Free",
        location: { lat: 19.9105, lng: 99.8406 },
        images: [],
        tips: ["Try northern Thai food", "Live performances", "Great souvenirs"],

        tags: [
            "chiang rai night bazaar",
            "night markets thailand",
            "chiang rai street food",
            "things to do in chiang rai at night",
            "northern thailand markets",
            "chiang rai shopping"
        ],

        searchableText:
            "chiang rai night bazaar thailand night market chiang rai street food northern thailand markets evening things to do in chiang rai live performances"
    }
];

export const GUIDE_METADATA_TO_UPDATE = {
    "thailand": {
        tags: [
            "thailand",
            "thailand travel guide",
            "thailand trip planner",
            "thailand itinerary",
            "2 week thailand itinerary",
            "10 day thailand itinerary",
            "best places in thailand",
            "things to do in thailand",
            "bangkok thailand",
            "phuket thailand",
            "chiang mai thailand",
            "krabi thailand",
            "koh samui",
            "pattaya thailand",
            "chiang rai thailand",
            "ayutthaya thailand",
            "thai islands",
            "island hopping thailand",
            "thailand beaches",
            "thailand temples",
            "thai street food",
            "backpacking thailand",
            "budget travel thailand",
            "luxury travel thailand",
            "honeymoon thailand",
            "thailand nightlife",
            "full moon party thailand",
            "southeast asia travel",
            "first time thailand",
            "digital nomad thailand",
            "thailand visa guide",
            "best time to visit thailand",
            "thailand weather guide",
            "thailand travel tips",
            "thailand food guide",
            "thai culture",
            "thailand family travel",
            "solo travel thailand",
            "thailand resorts",
            "thailand islands guide"
        ],

        searchableText:
            "thailand travel guide thailand itinerary 2 week thailand itinerary best places in thailand bangkok phuket chiang mai krabi koh samui pattaya chiang rai ayutthaya thailand beaches temples island hopping thai street food backpacking thailand honeymoon thailand digital nomad thailand southeast asia travel best time to visit thailand thailand visa travel tips family solo luxury budget travel"
    }

};

const generatePlaceMetadata = (place) => {
    const name = place.name?.toLowerCase() || "";
    const city = place.city?.toLowerCase() || "";
    const country = place.country?.toLowerCase() || "";
    const type = place.type?.toLowerCase() || "";
    const description = place.shortDescription?.toLowerCase() || "";

    const baseTags = [
        name,
        `${name} ${city}`,
        `${name} ${country}`,
        `things to do in ${name}`,
        `${type} in ${city}`,
        `${city} attractions`,
        `${country} travel`,
    ];

    const tags = [...new Set(baseTags.filter(Boolean))];

    const searchableText = [
        name,
        city,
        country,
        type,
        description,
        ...place.tips || []
    ]
        .join(" ")
        .toLowerCase();

    return { tags, searchableText };
};

const updateAllPlacesMetadata = async () => {
    const snapshot = await getDocs(collection(db, "places"));

    for (const docSnap of snapshot.docs) {
        const place = docSnap.data();

        const { tags, searchableText } = generatePlaceMetadata(place);

        await updateDoc(doc(db, "places", docSnap.id), {
            tags,
            searchableText,
            updatedAt: new Date()
        });

        console.log("Updated:", place.slug);
    }

    alert("All places auto-updated 🚀");
};


export default function GuideModal({ open, onClose, currentUserId }) {
    const titleRef = useRef(null);
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [country, setCountry] = useState("");
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [guideJsonInput, setGuideJsonInput] = useState("");
    const [placesJsonInput, setPlacesJsonInput] = useState("");


    useEffect(() => {
        if (open) {
            setTimeout(() => titleRef.current?.focus(), 50);
        }
    }, [open]);

    if (!open) return null;

    const resetForm = () => {
        setTitle("");
        setSlug("");
        setCountry("");
        setGuideJsonInput("");
        setPlacesJsonInput("");
        setError(null);
        setSaving(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSave = async () => {
        if (!guideJsonInput.trim() && !placesJsonInput.trim()) {
            setError("Nothing to save");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (guideJsonInput.trim()) {
                await uploadGuide();
            }

            if (placesJsonInput.trim()) {
                await uploadPlaces();
            }

            alert("Saved successfully ✅");
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };
    const uploadGuide = async () => {
        try {
            if (!auth.currentUser) {
                throw new Error("You must be logged in");
            }

            if (!guideJsonInput.trim()) {
                throw new Error("Guide JSON is empty");
            }

            setSaving(true);
            setError(null);

            const parsedJSON = JSON.parse(guideJsonInput);
            const normalizedSlug = normalizeSlug(
                slug || parsedJSON.slug || title
            );

            await setDoc(
                doc(db, "guides", normalizedSlug),
                {
                    ...parsedJSON,
                    id: normalizedSlug,
                    slug: normalizedSlug,
                    createdBy: auth.currentUser.uid,

                    // 🔥 Social Metrics (Initialize once)
                    likesCount: 0,
                    views: 0,
                    savesCount: 0,

                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false } // important for clean creation
            );
            await setDoc(
                doc(db, "slugs", normalizedSlug),
                {
                    guideId: normalizedSlug,
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to upload guide");
            throw err; // important so handleSave knows it failed
        } finally {
            setSaving(false);
        }
    };

    const uploadPlaces = async () => {
        if (!auth.currentUser) {
            throw new Error("You must be logged in");
        }

        if (!placesJsonInput.trim()) {
            throw new Error("Places JSON is empty");
        }

        const places = JSON.parse(placesJsonInput);
        if (!Array.isArray(places)) {
            throw new Error("Places JSON must be an array");
        }

        const batch = writeBatch(db);

        places.forEach((place) => {
            if (!place.id || !place.name) {
                throw new Error("Each place must have id and name");
            }

            const placeSlug = normalizeSlug(place.slug || place.name);

            // 1️⃣ Save place document
            batch.set(
                doc(db, "places", place.id),
                {
                    ...place,
                    slug: placeSlug,
                    createdBy: auth.currentUser.uid,

                    likesCount: 0,
                    views: 0,
                    savesCount: 0,

                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false }
            );

            // 2️⃣ Register slug
            batch.set(
                doc(db, "slugs", placeSlug),
                {
                    placeId: place.id, // ✅ new supported type
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );
        });

        await batch.commit();
    };

    const loadGuideJSON = (json) => {
        try {
            setTitle(json.title || "");
            setSlug(json.slug || json.id || "");
            setCountry(json.country?.name || "");
            setGuideJsonInput(JSON.stringify(json, null, 2));
            setError(null);
        } catch {
            setError("Invalid Guide JSON");
        }
    };
    const loadPlacesJSON = (placesArray) => {
        if (!Array.isArray(placesArray)) {
            setError("Places JSON must be an array");
            return;
        }
        setPlacesJsonInput(JSON.stringify(placesArray, null, 2));
    };
    const fixAllPlacesCounters = async () => {
        if (!auth.currentUser) {
            alert("You must be logged in");
            return;
        }

        if (!window.confirm("This will update ALL places. Continue?")) {
            return;
        }

        try {
            setSaving(true);

            const snap = await getDocs(collection(db, "places"));

            let updatedCount = 0;

            for (const docSnap of snap.docs) {
                const data = docSnap.data();

                const needsUpdate =
                    typeof data.likesCount !== "number" ||
                    typeof data.savesCount !== "number" ||
                    typeof data.views !== "number";

                if (needsUpdate) {
                    await updateDoc(docSnap.ref, {
                        likesCount:
                            typeof data.likesCount === "number"
                                ? data.likesCount
                                : 0,
                        savesCount:
                            typeof data.savesCount === "number"
                                ? data.savesCount
                                : 0,
                        views:
                            typeof data.views === "number"
                                ? data.views
                                : 0,
                    });

                    updatedCount++;
                }
            }

            alert(`✅ Updated ${updatedCount} places`);
        } catch (err) {
            console.error(err);
            alert("Error updating places");
        } finally {
            setSaving(false);
        }
    };
    const updateGuideMetadata = async () => {
        if (!auth.currentUser) {
            alert("You must be logged in");
            return;
        }

        try {
            setSaving(true);

            const slugs = Object.keys(GUIDE_METADATA_TO_UPDATE);

            if (!slugs.length) {
                alert("No guides defined in GUIDE_METADATA_TO_UPDATE");
                return;
            }

            for (const guideSlug of slugs) {
                const metadata = GUIDE_METADATA_TO_UPDATE[guideSlug];

                await updateDoc(doc(db, "guides", guideSlug), {
                    tags: metadata.tags,
                    searchableText: metadata.searchableText.toLowerCase(),
                    updatedAt: serverTimestamp(),
                });
            }

            alert("✅ All guide metadata updated successfully");

        } catch (err) {
            console.error(err);
            alert("Error updating guide metadata");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal-backdrop"
            onMouseDown={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="modal guide-modal">
                <h3>Create Guide</h3>

                {/* Title */}
                <label>Title *</label>
                <input
                    ref={titleRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="France Travel Guide"
                />

                {/* Slug */}
                <label>Slug (optional)</label>
                <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="france"
                />

                {/* Country */}
                <label>Country</label>
                <input
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="France"
                />

                {/* JSON */}
                <label>Guide JSON *</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={guideJsonInput}
                    onChange={(e) => setGuideJsonInput(e.target.value)}
                    placeholder="{ ...full guide json... }"
                />

                <label>Places JSON</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={placesJsonInput}
                    onChange={(e) => setPlacesJsonInput(e.target.value)}
                    placeholder="[ { ...place }, { ...place } ]"
                />

                {error && <div className="error">{error}</div>}

                <div className="controls">
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Guide"}
                    </button>
                    <div className="json-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadGuideJSON(GUIDE_JSON)}
                        >
                            📘 Load Guide JSON
                        </button>

                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadPlacesJSON(PLACES_FOR_UPLOAD)}
                        >
                            📍 Load Places JSON
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={fixAllPlacesCounters}
                        disabled={saving}
                    >
                        🔧 Add likes,viewcount,savecount
                    </button>


                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={updateGuideMetadata}
                        disabled={saving}
                    >
                        🏷 Update Tags + Search Text
                    </button>
                    <button onClick={updateAllPlacesMetadata}>
                        Auto Generate Metadata
                    </button>

                </div>
            </div>
        </div>
    );
}
